const USER_BASE = '/r/7qv9n';
const ADMIN_BASE = '/r/3mk8x';
const SITE_BASE = '/r/5pt2r';
const INVITE_BASE = '/r/8jn6p';
const EVENT_BASE = '/r/4fd1e';

export const APP_ROUTE_PREFIXES = {
  user: USER_BASE,
  admin: ADMIN_BASE,
  site: SITE_BASE,
  invite: INVITE_BASE,
  event: EVENT_BASE,
};

export const APP_ROUTES = {
  home: '/',
  login: '/auth/in',
  register: '/auth/up',
  forgotPassword: '/auth/recover',
  resetPassword: (uid = ':uid', token = ':token') => `/auth/reset/${uid}/${token}`,
  dashboard: `${USER_BASE}/d`,
  profile: `${USER_BASE}/p`,
  twoFactorSetup: `${USER_BASE}/g`,
  circles: `${USER_BASE}/c`,
  circleDetail: (id = ':id') => `${USER_BASE}/c/${id}`,
  events: `${USER_BASE}/e`,
  eventDetail: (id = ':id') => `${EVENT_BASE}/e/${id}`,
  eventPaymentSuccess: (id = ':id') => `${EVENT_BASE}/e/${id}/payment/success`,
  eventPaymentCancelled: (id = ':id') => `${EVENT_BASE}/e/${id}/payment/cancelled`,
  admin: `${ADMIN_BASE}/o`,
  adminMembers: `${ADMIN_BASE}/m`,
  adminInvites: `${ADMIN_BASE}/i`,
  adminEvents: `${ADMIN_BASE}/e`,
  adminEventSignups: (id = ':id') => `${ADMIN_BASE}/e/${id}/s`,
  site: `${SITE_BASE}/o`,
  siteCircles: `${SITE_BASE}/c`,
  siteDatabase: `${SITE_BASE}/d`,
  siteDatabaseExport: `${SITE_BASE}/d/x`,
  siteUserDetail: (id = ':id') => `${SITE_BASE}/u/${id}`,
  inviteAccept: (token = ':token') => `${INVITE_BASE}/j/${token}`,
  privacy: '/privacy',
  cookies: '/cookies',
  forbidden: '/forbidden',
};

const LEGACY_ROUTE_MAPPERS = [
  { pattern: /^\/login\/?$/, build: () => APP_ROUTES.login },
  { pattern: /^\/register\/?$/, build: () => APP_ROUTES.register },
  { pattern: /^\/forgot-password\/?$/, build: () => APP_ROUTES.forgotPassword },
  { pattern: /^\/reset-password\/([^/]+)\/([^/]+)\/?$/, build: (match) => APP_ROUTES.resetPassword(match[1], match[2]) },
  { pattern: /^\/dashboard\/?$/, build: () => APP_ROUTES.dashboard },
  { pattern: /^\/profile\/?$/, build: () => APP_ROUTES.profile },
  { pattern: /^\/2fa-setup\/?$/, build: () => APP_ROUTES.twoFactorSetup },
  { pattern: /^\/circles\/?$/, build: () => APP_ROUTES.circles },
  { pattern: /^\/circles\/([^/]+)\/?$/, build: (match) => APP_ROUTES.circleDetail(match[1]) },
  { pattern: /^\/(?:events|public-events)\/?$/, build: () => APP_ROUTES.events },
  { pattern: /^\/events\/([^/]+)\/?$/, build: (match) => APP_ROUTES.eventDetail(match[1]) },
  { pattern: /^\/events\/([^/]+)\/payment\/success\/?$/, build: (match) => APP_ROUTES.eventPaymentSuccess(match[1]) },
  { pattern: /^\/events\/([^/]+)\/payment\/cancelled\/?$/, build: (match) => APP_ROUTES.eventPaymentCancelled(match[1]) },
  { pattern: /^\/invite\/([^/]+)\/?$/, build: (match) => APP_ROUTES.inviteAccept(match[1]) },
  { pattern: /^\/admin\/?$/, build: () => APP_ROUTES.admin },
  { pattern: /^\/admin\/members\/?$/, build: () => APP_ROUTES.adminMembers },
  { pattern: /^\/admin\/invites\/?$/, build: () => APP_ROUTES.adminInvites },
  { pattern: /^\/admin\/events\/?$/, build: () => APP_ROUTES.adminEvents },
  { pattern: /^\/admin\/events\/([^/]+)\/signups\/?$/, build: (match) => APP_ROUTES.adminEventSignups(match[1]) },
  { pattern: /^\/site\/?$/, build: () => APP_ROUTES.site },
  { pattern: /^\/site\/circles\/?$/, build: () => APP_ROUTES.siteCircles },
  { pattern: /^\/(?:site\/database|db)\/?$/, build: () => APP_ROUTES.siteDatabase },
  { pattern: /^\/(?:site\/database\/export|db\/export)\/?$/, build: () => APP_ROUTES.siteDatabaseExport },
  { pattern: /^\/site\/users\/([^/]+)\/?$/, build: (match) => APP_ROUTES.siteUserDetail(match[1]) },
  { pattern: /^\/privacy\/?$/, build: () => APP_ROUTES.privacy },
  { pattern: /^\/cookies\/?$/, build: () => APP_ROUTES.cookies },
  { pattern: /^\/forbidden\/?$/, build: () => APP_ROUTES.forbidden },
];

function parseInternalPath(rawPath) {
  if (typeof rawPath !== 'string') return null;
  const trimmed = rawPath.trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  try {
    return new URL(trimmed, 'https://ringo.local');
  } catch {
    return null;
  }
}

function startsWithBase(pathname, base) {
  return pathname === base || pathname.startsWith(`${base}/`);
}

export function normalizeAppPath(rawPath) {
  const parsed = parseInternalPath(rawPath);
  if (!parsed) return '';

  const { pathname, search, hash } = parsed;
  const mapper = LEGACY_ROUTE_MAPPERS.find(({ pattern }) => pattern.test(pathname));
  const nextPathname = mapper ? mapper.build(pathname.match(mapper.pattern)) : pathname;

  return `${nextPathname}${search}${hash}`;
}

export function getDefaultRouteForView(view) {
  if (view === 'admin') return APP_ROUTES.admin;
  if (view === 'site') return APP_ROUTES.site;
  return APP_ROUTES.dashboard;
}

export function getSectionViewForPath(pathname) {
  const normalized = normalizeAppPath(pathname);
  const parsed = parseInternalPath(normalized || pathname);
  if (!parsed) return null;

  if (startsWithBase(parsed.pathname, ADMIN_BASE)) return 'admin';
  if (startsWithBase(parsed.pathname, SITE_BASE)) return 'site';
  if (startsWithBase(parsed.pathname, USER_BASE) || startsWithBase(parsed.pathname, EVENT_BASE)) return 'user';
  return null;
}

export function getVisibleSegments(pathname) {
  const normalized = normalizeAppPath(pathname);
  const parsed = parseInternalPath(normalized || pathname);
  if (!parsed) return [];

  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments.length >= 2 && segments[0] === 'r') {
    return segments.slice(2);
  }
  return segments;
}
